import { type CSSInterpolation } from '@emotion/serialize'

import { getPrettyRunStatus } from 'components/services/service-utils'
import { muiTheme } from 'mui-theme'
import { PolicyResult } from 'types/api/policies/policy'

import type { RiskSummaryStats } from 'types/api/services/service'

export const DONUT_WIDTH = 200
export const REPORT_CHART_LOADING_BOX_HEIGHT = 360

export const RELIABILITY_REPORT_COLORS = {
  SCALABILITY: muiTheme.palette.marketingViolet650.main,
  REDUNDANCY: muiTheme.palette.orchid.dark,
  DEPENDENCIES: muiTheme.palette.marketingBlue400.main,
  RISKS: muiTheme.palette.oceanBlue.main,
  OTHER: muiTheme.palette.marketingViolet200.main,
  EXTRA_CREDIT: muiTheme.palette.orchid.light
}

export const reportTableStyles = {
  '& .MuiDataGrid-cell:focus': {
    outline: 0
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': {
    width: '100%',
    justifyContent: 'center'
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'initial',
    lineHeight: 'normal',
    textAlign: 'center',
    // These styles are necessary to prevent the column header from clipping when there are more than 4 lines of text
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical'
  },
  border: 'none'
}

/**
 * EN-6602 - Styling to allow for horizontal scrolling for team reports
 */
export const PAGE_SECTION_REPORT_STYLES: CSSInterpolation = {
  display: 'block',
  flex: 'unset',
  marginLeft: '-42px', // undo some padding set by Content
  overflowX: 'auto', // only show scroll if width is excessive
  overflowY: 'hidden',
  width: 'calc(100vw - 300px)' // 207px: width of left app sidebar plus Content padding
}

export function getTeamReliabilityReportDependencyTooltipTitle(
  runStatus: PolicyResult
) {
  if (runStatus === 'FAILED') {
    return '1 or more dependencies in this service has a non-passing state'
  }

  if (runStatus === 'NEVER_RUN') {
    return 'Never tested'
  }

  return getPrettyRunStatus(runStatus)
}

export function getResultBySeverity(
  resultSet: Set<PolicyResult>
): PolicyResult {
  // Defines the order of severity for policy results
  // The first matching result in this order will be returned
  const severityOrder: PolicyResult[] = [
    PolicyResult.FAILED,
    PolicyResult.EXPIRED,
    PolicyResult.NEVER_RUN,
    PolicyResult.IGNORED,
    PolicyResult.PASSED
  ]

  for (const result of severityOrder) {
    if (resultSet.has(result)) {
      return result
    }
  }

  // Default case if no results match the severity order
  return PolicyResult.IGNORED
}

export function getRiskDelta(riskSummaryStats: RiskSummaryStats) {
  const { atRisk, previousIntervalAtRisk } = riskSummaryStats

  return previousIntervalAtRisk >= 0
    ? atRisk - previousIntervalAtRisk
    : undefined
}
